import { Button, ButtonColor, Container, ContainerSize, Gap, Heading, JustifyContent, Spinner, SpinnerColor, SpinnerSize, SpinnerType, Stack, Text, Wrapper } from '@rebootui';
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type SpinnerProps = {}

const SpinnerPage = ({ }: SpinnerProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Spinners
            </Heading>

            <Text className="mb-4">
                The <Text el="strong">Spinner</Text> component is used to indicate loading or ongoing activity in your
                UI. It supports different styles, colors, sizes, and accessibility features.
            </Text>
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Usage
            </Heading>
            <Text className="mb-4">
                The default <Text el="strong">spinner</Text> is a <Text el="strong">border spinner</Text> also no color
                or size is applied unless explicitly specified.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Spinner />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Colors
            </Heading>
            <Text className="mb-4">
                The default <Text el="strong">spinner</Text> is a <Text el="strong">border spinner</Text> also no color
                or size is applied unless explicitly specified.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Spinner color={SpinnerColor.Primary} />
                        <Spinner color={SpinnerColor.Secondary} />
                        <Spinner color={SpinnerColor.Success} />
                        <Spinner color={SpinnerColor.Danger} />
                        <Spinner color={SpinnerColor.Warning} />
                        <Spinner color={SpinnerColor.Info} />
                        <Spinner color={SpinnerColor.Light} />
                        <Spinner color={SpinnerColor.Dark} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerBorderColorCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Growing spinner
            </Heading>
            <Text className="mb-4">
                You can create a <Text el="strong">growing spinner</Text> by setting <Text
                    el="strong">spinnerType</Text> to <Text el="strong">SpinnerType.Grow</Text>.Unlike the default border
                spinner, the growing spinner animates by expanding and fading.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Spinner spinnerType={SpinnerType.Grow} />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerGrowCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Text className="mb-4">You can set the <Text el="strong">color</Text> of a <Text el="strong">growing
                spinner</Text> by using the <Text el="strong">color prop</Text>.</Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Primary} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Secondary} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Success} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Danger} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Warning} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Info} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Light} />
                        <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Dark} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerGrowColorCode}
                </CodeHighlighter>
            </CodeSnippet>


            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Usage of utilities classes
            </Heading>
            <Text className="mb-4">
                You can apply utility classes to the spinner via the <Text el="strong">className prop</Text> to control
                spacing, alignment, or other styling.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Spinner className="m-5" />
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerUtilitiesClassCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Placement
            </Heading>
            <Text className="mb-4">
                You can control the placement of the <Text el="strong">spinner</Text> within a container by using <Text
                    el="strong">layout components</Text> and <Text el="strong">alignment props</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack justifyContent={JustifyContent.Center}>
                        <Spinner />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerPlacementCode}
                </CodeHighlighter>
            </CodeSnippet>

            <CodeSnippet>
                <CodePreview>
                    <Stack justifyContent={JustifyContent.Between}>
                        <Text>Loading...</Text>
                        <Spinner />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerPlacementTextCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Text align
            </Heading>
            <Text className="mb-4">
                You can also center a spinner by wrapping it in a container and applying text alignment or <Text
                    el="strong">layout classes</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Wrapper className="text-center">
                        <Spinner />
                    </Wrapper>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerTextCenterCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Size
            </Heading>
            <Text className="mb-4">
                You can control the <Text el="strong">size</Text> of the spinner using the <Text el="strong">size
                    prop</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Spinner size={SpinnerSize.SpinnerBorderSm} />
                        <Spinner spinnerType={SpinnerType.Grow} size={SpinnerSize.SpinnerGrowSm} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerSizeCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Custom CSS
            </Heading>
            <Text className="mb-4">
                You can set a <Text el="strong">custom size</Text> for the <Text el="strong">spinner</Text> using
                the <Text el="strong">style prop</Text>. This allows precise control over width and height beyond the
                predefined size options.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Spinner style={{ width: '3rem', height: '3rem' }} />
                        <Spinner spinnerType={SpinnerType.Grow} style={{ width: '3rem', height: '3rem' }} />
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerCustomCssCode}
                </CodeHighlighter>
            </CodeSnippet>

            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Buttons
            </Heading>
            <Text className="mb-4">
                You can place a spinner inside a <Text el="strong">button</Text> to indicate a loading state while an
                action is in <Text el="strong">progress</Text>.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary} disabled={true}>
                            <Spinner size={SpinnerSize.SpinnerBorderSm} />
                        </Button>
                        <Button color={ButtonColor.Primary} disabled={true}>
                            <Spinner size={SpinnerSize.SpinnerBorderSm} className="me-1" />
                            Loading...
                        </Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerBorderButtonCode}
                </CodeHighlighter>
            </CodeSnippet>
            <CodeSnippet>
                <CodePreview>
                    <Stack gap={Gap.Gap2}>
                        <Button color={ButtonColor.Primary} disabled={true}>
                            <Spinner spinnerType={SpinnerType.Grow} size={SpinnerSize.SpinnerGrowSm} />
                        </Button>
                        <Button color={ButtonColor.Primary} disabled={true}>
                            <Spinner spinnerType={SpinnerType.Grow} size={SpinnerSize.SpinnerGrowSm} className="me-1" />
                            Loading...
                        </Button>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {SpinnerGrowButtonCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    )
}

export default SpinnerPage;

const SpinnerCode = `<Spinner/>`;

const SpinnerBorderColorCode = `<Stack gap={Gap.Gap2}>
    <Spinner color={SpinnerColor.Primary}/>
    <Spinner color={SpinnerColor.Secondary}/>
    <Spinner color={SpinnerColor.Success}/>
    <Spinner color={SpinnerColor.Danger}/>
    <Spinner color={SpinnerColor.Warning}/>
    <Spinner color={SpinnerColor.Info}/>
    <Spinner color={SpinnerColor.Light}/>
    <Spinner color={SpinnerColor.Dark}/>
</Stack>`;

const SpinnerGrowCode = `<Spinner spinnerType={SpinnerType.Grow}/>`;

const SpinnerGrowColorCode = `<Stack gap={Gap.Gap2}>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Primary}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Secondary}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Success}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Danger}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Warning}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Info}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Light}/>
    <Spinner spinnerType={SpinnerType.Grow} color={SpinnerColor.Dark}/>
</Stack>`;

const SpinnerUtilitiesClassCode = `<Spinner className="m-5"/>`;

const SpinnerPlacementCode = `<Stack justifyContent={JustifyContent.Center}>
    <Spinner/>
</Stack>`;

const SpinnerPlacementTextCode = `<Stack justifyContent={JustifyContent.Between}>
    <Text>Loading...</Text>
    <Spinner/>
</Stack>`;

const SpinnerTextCenterCode = `<Wrapper className="text-center">
    <Spinner/>
</Wrapper>`;

const SpinnerSizeCode = `<Stack>
    <Stack gap={Gap.Gap2}>
        <Spinner size={SpinnerSize.SpinnerBorderSm}/>
        <Spinner spinnerType={SpinnerType.Grow} size={SpinnerSize.SpinnerGrowSm}/>
    </Stack>
</Stack>`;

const SpinnerCustomCssCode = `<Stack gap={Gap.Gap2}>
    <Spinner style={{width: '3rem', height: '3rem'}}/>
    <Spinner spinnerType={SpinnerType.Grow} style={{width: '3rem', height: '3rem'}}/>
</Stack>`;

const SpinnerBorderButtonCode = `<Stack gap={Gap.Gap2}>
    <Button color={ButtonColor.Primary} disabled={true}>
        <Spinner size={SpinnerSize.SpinnerBorderSm}/>
    </Button>
    <Button color={ButtonColor.Primary} disabled={true}>
        <Spinner size={SpinnerSize.SpinnerBorderSm} className="me-1"/>
        Loading...
    </Button>
</Stack>`;

const SpinnerGrowButtonCode = `<Stack gap={Gap.Gap2}>
    <Button color={ButtonColor.Primary} disabled={true}>
        <Spinner size={SpinnerSize.SpinnerGrowSm}/>
    </Button>
    <Button color={ButtonColor.Primary} disabled={true}>
        <Spinner size={SpinnerSize.SpinnerGrowSm} className="me-1"/>
        Loading...
    </Button>
</Stack>`;

